import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { S3UploadService } from 'src/app/core/services/s3-upload/s3-upload.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { S3BucketName } from 'src/app/shared/models/constants/s3-bucket-name';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import {
  MigrationUploadFile,
  MigrationUploadFileStatus,
  TMigrationUploadFileDataType,
  MigrationUploadFileSupplyType,
  PortPointsExecution,
} from 'src/app/shared/models/migration-upload-file/migration-upload-file';
import { DataService } from '../../../../core/api/data/data.service';
import { PortPointsExecutionHeadComponent } from '../port-points-execution-head/port-points-execution-head.component';
import { RewardService } from '../../../../core/api/reward/reward.service';
import { SnackBarService } from '../../../../core/services/snackbar/snackbar.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-upload-migration',
  templateUrl: './upload-migration.component.html',
  styleUrls: ['./upload-migration.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Upload Migration component
 */
export class UploadMigrationComponent {
  private readonly csvMimeType = 'text/csv';
  private readonly xlsxMimeType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  labels = {
    upload: 'upload',
    packageUpload: 'package-upload-title',
    migrationUpload: 'migration-upload-title',
    portUpload: 'port-upload-title',
    uploadFunctionExplanation: 'upload-function-explanation',
    selectDataUpload: 'select-which-data-you-want-to-upload',
    uploadQueue: 'upload-queue',
    inkSupplies: 'ink-supplies',
    paperSupplies: 'paper-supplies',
    uploadFile: 'upload-file',
    chooseFile: 'choose-file',
    noFileChosen: 'no-file-chosen',
    legacyAccountData: 'legacy-account-data',
    legacyPointBalanceData: 'legacy-point-balance-data',
    onlyUploadFilesInBeforeFormat: 'only-upload-files-in--beforeformat',
    onlyUploadFilesInAfterFormat: 'only-upload-files-in--afterformat',
    uploadPortReportInfo: 'upload-port-report-info',
    clickHereToDownload: 'click-here-to-download',
    migrationDataspreadSheetTemplate: 'migration-data-spreadsheet-template',
    portBatchProcessing: 'port-batch-processing-title',
    triggerPortProcessing: 'trigger-port-points-processing',
    portProcessingInProgress: 'port-points-processing-in-progress',
    portProcessingStarted: 'port-points-processing-started',
    portProcessingAlreadyRunning: 'port-points-processing-already-running',
    unexpectedError: 'unexpected-error',
  };
  queueTitle = this.labels.packageUpload;
  queueType: S3BucketName | string = S3BucketName.POINT_CODE;
  showPortText: boolean = false;
  showMigrationText: boolean = false;
  showPortBatchProcessing: boolean = false;

  formGroup = new FormGroup({
    dataType: new FormControl<TMigrationUploadFileDataType>(
      S3BucketName.POINT_CODE,
      [Validators.required]
    ),
    supplyType: new FormControl<MigrationUploadFileSupplyType>(
      MigrationUploadFileSupplyType.INK,
      [Validators.required]
    ),
    file: new FormControl<File | null>(null, [Validators.required]),
  });

  filters: FilterOptions = {
    packageCodes: false,
    accountData: false,
    portReport: false,
    portBatchProcessing: false,
  };

  chipContents: ChipContent[] = this.chipContent();
  @ViewChild(PortPointsExecutionHeadComponent)
  portPointsExecutionHead?: PortPointsExecutionHeadComponent; // eslint-disable-line indent

  triggeringPortProcessing = false;
  portProcessing = false;
  /**
   *@constructor
   */
  constructor(
    private readonly s3UploadService: S3UploadService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly dataService: DataService,
    private readonly rewardService: RewardService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * Supply type control from form group
   */
  get supplyTypeControl() {
    return this.formGroup.controls.supplyType;
  }

  /**
   * Data type control from form group
   */
  get dataTypeControl() {
    return this.formGroup.controls.dataType;
  }

  /**
   * File control fom form group
   */
  get fileControl() {
    return this.formGroup.controls.file;
  }

  /**
   * Uploads chosen file, if available
   * @param {number} tries
   * @param {MigrationUploadFile?} migrationUploadFile object that needs updating
   */
  uploadFile(tries: number = 1, migrationUploadFile?: MigrationUploadFile) {
    const adminRegion = this.authenticatedUserService.currentRegion?.id;
    if (adminRegion === undefined) {
      throw Error(
        'upload-migration.component.ts:uploadFile -> adminRegion is undefined'
      );
    }
    const file =
      migrationUploadFile?.file || this.fileControl.value || undefined;
    if (file === undefined) {
      return;
    }
    if (migrationUploadFile?.file === undefined) {
      this.fileControl.setValue(null);
    }
    if (migrationUploadFile === undefined) {
      migrationUploadFile = new MigrationUploadFile(
        file.name,
        this.dataTypeControl.value!,
        this.supplyTypeControl.value!,
        MigrationUploadFileStatus.PENDING,
        new Date(),
        adminRegion,
        file
      );
    }
    this.s3UploadService.upload(tries, migrationUploadFile);
  }

  /**
   * Retry action
   * @param {MigrationUploadFile} migrationUploadFile
   */
  retry(migrationUploadFile: MigrationUploadFile) {
    if (migrationUploadFile.status === MigrationUploadFileStatus.RETRYING) {
      return;
    }
    migrationUploadFile.status = MigrationUploadFileStatus.RETRYING;
    this.s3UploadService.upload(2, migrationUploadFile);
  }

  /**
   * Changes data type to a chip's value
   * @param {FilterOptions} filters
   */
  updateFilter(filters: FilterOptions) {
    let dataType: S3BucketName | undefined = undefined;
    let supplyType: MigrationUploadFileSupplyType | undefined = undefined;
    const { packageCodes, accountData, portReport, portBatchProcessing } =
      filters;
    if (packageCodes) {
      this.queueTitle = this.labels.packageUpload;
      dataType = S3BucketName.POINT_CODE;
      this.queueType = S3BucketName.POINT_CODE;
      supplyType = MigrationUploadFileSupplyType.INK;
      this.showPortText = false;
      this.showMigrationText = false;
      this.showPortBatchProcessing = false;
    } else if (accountData) {
      this.queueTitle = this.labels.migrationUpload;
      dataType = S3BucketName.ACCOUNTS;
      this.queueType = S3BucketName.ACCOUNTS;
      supplyType = MigrationUploadFileSupplyType.ACCOUNTS;
      this.showPortText = false;
      this.showMigrationText = true;
      this.showPortBatchProcessing = false;
    } else if (portReport) {
      dataType = S3BucketName.PORT_REPORT;
      this.queueTitle = this.labels.portUpload;
      this.queueType = S3BucketName.PORT_REPORT;
      supplyType = MigrationUploadFileSupplyType.INK;
      this.showPortText = true;
      this.showMigrationText = false;
      this.showPortBatchProcessing = false;
    } else if (portBatchProcessing) {
      this.showMigrationText = false;
      this.showPortText = false;
      this.showPortBatchProcessing = true;
      this.queueTitle = this.labels.portBatchProcessing;
      this.fileControl.setValue(null);
      return;
    }
    if (this.dataTypeControl.value !== dataType) {
      this.dataTypeControl.setValue(dataType || null);
      this.supplyTypeControl.setValue(supplyType || null);
    }
  }

  /**
   * Downloads a given report
   * @param {MigrationUploadFile} migrationFile
   */
  downloadReport(migrationFile: MigrationUploadFile) {
    this.s3UploadService.download(migrationFile);
  }

  /**
   * Downloads migration template
   */
  downloadMigrationTemplate() {
    this.s3UploadService.downloadMigrationTemplate();
  }

  /**
   * Gets accepted mime type based on data type chosen by user
   */
  get acceptedMimeType() {
    if (
      this.dataTypeControl.value === S3BucketName.ACCOUNTS ||
      this.dataTypeControl.value === S3BucketName.PORT_REPORT ||
      this.dataTypeControl.value === S3BucketName.POINT_CODE
    ) {
      return this.csvMimeType;
    } else {
      return this.xlsxMimeType;
    }
  }

  /**
   * Gets upload file type
   */
  get uploadFileType() {
    if (this.acceptedMimeType === this.csvMimeType) {
      return 'CSV';
    }
    return 'XLSX';
  }

  /**
   * Gets chip content
   * @return {ChipContent[]}
   */
  private chipContent(): ChipContent[] {
    let items: ChipContent[] = [];
    if (this.authenticatedUserService.isGlobalAdmin()) {
      items = [
        ChipContent.PackageCodesChip(),
        ChipContent.AccountDataChip(),
        ChipContent.PortReportChip(),
        ChipContent.PortBatchProcessingChip(),
      ];
    } else if (this.authenticatedUserService.isRegionalAdmin()) {
      items = [
        ChipContent.PortReportChip(),
        ChipContent.PortBatchProcessingChip(),
      ];
      this.updateFilter({
        packageCodes: false,
        accountData: false,
        portReport: true,
        portBatchProcessing: false,
      });
    }
    return items;
  }

  /**
   * Checks if it has single option
   * @return {boolean}
   */
  isSingleOption(): boolean {
    return this.chipContents.length < 2 ? true : false;
  }

  /**
   * Downloads the report for a PORT batch execution
   * @param {PortPointsExecution} execution
   */
  downloadPortBatchReport(execution: PortPointsExecution): void {
    if (!execution.reportFileName) {
      return;
    }
    this.dataService
      .getPortPointsReportDownloadURL(execution.reportFileName)
      .subscribe((downloadUrl) => {
        window.open(downloadUrl, '_blank');
      });
  }
  /**
   * Updates whether PORT points processing is currently running.
   *
   * @param {boolean} processing Processing state.
   * @return {void}
   */
  updatePortProcessingState(processing: boolean): void {
    this.portProcessing = processing;
  }

  /**
   * Triggers PORT points processing manually.
   *
   * @return {void}
   */
  triggerPortPointsProcessing(): void {
    if (this.triggeringPortProcessing || this.portProcessing) {
      return;
    }

    this.triggeringPortProcessing = true;

    this.rewardService.startPortPointsProcessing().subscribe({
      next: () => {
        this.portProcessing = true;
        this.snackBarService.success(this.labels.portProcessingStarted);
        this.portPointsExecutionHead?.refreshExecutions();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          this.portProcessing = true;
          this.snackBarService.error(this.labels.portProcessingAlreadyRunning);
          this.portPointsExecutionHead?.refreshExecutions();
        } else {
          this.snackBarService.error(this.labels.unexpectedError);
        }
        this.triggeringPortProcessing = false;
      },
    });
  }
}
